'use client'

import React, { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from 'framer-motion'
import AppStoreLink from './AppStoreLink'

const navLinks = [
  // The product database is the front door for "does <product> have
  // microplastics" traffic and the only free surface a Google visitor can
  // actually use before installing anything, so it takes the first slot.
  { href: '/products', label: 'Product Database' },
  { href: '/microplastics-guide', label: 'Guide' },
  { href: '/microplastics-scanner-app', label: 'Scanner App' },
  // /blog is the chronological feed of every article. Labeled "Articles" so it
  // doesn't collide with /microplastics-research (the curated Research Hub).
  { href: '/blog', label: 'Articles' },
  // Free interactive tools — paste-in tools convert at higher rates than any
  // article. The ingredient checker is the single highest-intent surface on
  // the site (44% search CTR, ~7 checks per user), so it gets the nav slot
  // alongside the risk quiz.
  { href: '/microplastic-ingredient-checker', label: 'Ingredient Checker' },
  { href: '/risk-checker', label: 'Risk Quiz' },
  // FAQs moved to the footer in the August 2026 pass — seven desktop links
  // plus the logo and the install pill overflow the lg breakpoint, and FAQs
  // was the lowest-traffic of the seven.
]

export default function Navigation() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [hoveredLink, setHoveredLink] = useState<string | null>(null)
  const { scrollY } = useScroll()
  const pathname = usePathname() ?? '/'

  // Active-route detection: exact match for "/", prefix-match for sections so
  // /blog/foo correctly highlights the /blog tab in the nav.
  const isActive = (href: string) => {
    if (href === '/') return pathname === '/'
    return pathname === href || pathname.startsWith(href + '/')
  }

  // The homepage hero is a deep-forest panel that runs edge-to-edge under
  // this fixed nav. While the nav is transparent on top of it, links/logo
  // must render light-on-dark; once scrolled (cream bg) they flip back.
  const onDarkHero = pathname === '/' && !scrolled

  // Refs that drive focus management for the mobile dialog.
  // - menuRef: the dialog container; we toggle `inert` on it so it's removed
  //   from the tab order AND the accessibility tree when closed.
  // - triggerRef: the hamburger button — focus is returned here when the
  //   dialog closes, so keyboard users land back where they started.
  // - closeButtonRef: the first focusable inside the dialog; we move focus
  //   here on open so screen readers announce "Close menu" first.
  // - wasOpenRef: tracks the previous open/closed state across renders so
  //   we only restore focus on a real open→close transition (not on mount).
  const menuRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const wasOpenRef = useRef(false)

  useMotionValueEvent(scrollY, 'change', (latest) => {
    setScrolled(latest > 20)
  })

  useEffect(() => {
    if (mobileMenuOpen) {
      // Lock background scroll while the mobile menu is open.
      // touchAction prevents iOS Safari from rubber-banding the page behind.
      const prevOverflow = document.body.style.overflow
      const prevTouchAction = document.body.style.touchAction
      document.body.style.overflow = 'hidden'
      document.body.style.touchAction = 'none'
      return () => {
        document.body.style.overflow = prevOverflow
        document.body.style.touchAction = prevTouchAction
      }
    }
  }, [mobileMenuOpen])

  // Close the mobile menu when escape is pressed — standard a11y for dialogs.
  useEffect(() => {
    if (!mobileMenuOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileMenuOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mobileMenuOpen])

  // Toggle `inert` on the off-canvas menu. When closed, `inert` makes it
  // unfocusable and invisible to assistive tech (replacing aria-hidden, which
  // doesn't block tab focus). Universal browser support since 2023.
  useEffect(() => {
    const menu = menuRef.current
    if (!menu) return
    if (mobileMenuOpen) menu.removeAttribute('inert')
    else menu.setAttribute('inert', '')
  }, [mobileMenuOpen])

  // Focus-in on open, focus-out on close. Guarded by wasOpenRef so we don't
  // hijack focus on the initial render (where mobileMenuOpen starts false).
  useEffect(() => {
    if (mobileMenuOpen) {
      closeButtonRef.current?.focus()
    } else if (wasOpenRef.current) {
      triggerRef.current?.focus()
    }
    wasOpenRef.current = mobileMenuOpen
  }, [mobileMenuOpen])

  // Tab key trap: cycle focus inside the open dialog so it can't leak to
  // background content. Listening on `document` (not the menu) catches Tab
  // even when focus has somehow ended up outside.
  useEffect(() => {
    if (!mobileMenuOpen) return
    const menu = menuRef.current
    if (!menu) return

    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return
      const focusables = Array.from(
        menu.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      )
      if (focusables.length === 0) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement as HTMLElement | null

      if (e.shiftKey && (active === first || !menu.contains(active))) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && (active === last || !menu.contains(active))) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [mobileMenuOpen])

  return (
    <nav
      className={`fixed w-full top-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ease-out border-b border-transparent ${
        scrolled
          ? 'bg-cream/90 backdrop-blur-xl !border-black/[0.04] shadow-sm'
          : onDarkHero
            // Solid hero-green (not transparent) on the homepage hero: visually
            // identical to transparent-over-green, but it gives Safari 26+ a
            // fixed top-edge element to sample for the status-bar tint, so the
            // green runs under the iOS status bar instead of falling back to the
            // cream body background. See the homepage theme-color override too.
            ? 'bg-deepForest'
            : 'bg-transparent'
      }`}
    >
      <div className="relative">
        {/* `.frame` — the single shared page frame. The footer and every page
            section now resolve to this same left edge. */}
        <div className="frame">
          <div className="flex justify-between items-center h-16">
            <Link
              href="/"
              className="flex items-center gap-2.5 hover:opacity-80 transition-opacity duration-200 relative z-50 -ml-2 px-2 py-1 rounded-control pressable"
              aria-label="MicroPlastics home"
            >
              <Image src="/icon1.png" alt="" width={32} height={32} priority className="rounded-lg w-8 h-8" />
              <span className={`font-semibold text-base sm:text-lg tracking-tight ${onDarkHero ? 'text-cream' : 'text-charcoal'}`}>MicroPlastics</span>
            </Link>

            {/* Mobile menu button — 44x44 hit area meets WCAG touch target. */}
            <button
              ref={triggerRef}
              type="button"
              className={`lg:hidden tap-target -mr-2 inline-flex items-center justify-center rounded-control transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-warmOrange relative z-50 pressable ${
                onDarkHero
                  ? 'text-cream hover:text-goldLight hover:bg-white/10'
                  : 'text-gray-600 hover:text-warmOrange hover:bg-warmOrange/5'
              }`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-navigation"
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            >
              {mobileMenuOpen ? (
                <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>

            {/* Desktop navigation — active page gets aria-current + a visible
                dot indicator. We deliberately use BOTH font-weight and the dot
                so the active state is conveyed without relying on color alone
                (WCAG 1.4.1 Use of Color). */}
            <div
              className="hidden lg:flex gap-1"
              onMouseLeave={() => setHoveredLink(null)}
            >
              {navLinks.map((link) => {
                const active = isActive(link.href)
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    aria-current={active ? 'page' : undefined}
                    className={`relative inline-flex items-center px-4 py-2 min-h-[40px] text-sm transition-colors duration-200 rounded-control focus:outline-none focus-visible:ring-2 focus-visible:ring-warmOrange ${
                      onDarkHero
                        ? active
                          ? 'text-cream font-semibold'
                          : 'text-cream/70 font-medium hover:text-cream'
                        : active
                          ? 'text-charcoal font-semibold'
                          : 'text-gray-600 font-medium hover:text-charcoal'
                    }`}
                    onMouseEnter={() => setHoveredLink(link.href)}
                  >
                    {/* Sliding hover highlight.
                        Three things were wrong with this and all three matter
                        at the frequency a nav gets used:

                        1. `stiffness: 400, damping: 30` is a damping ratio of
                           ~0.75, i.e. it overshoots. Overshoot is the right
                           feel for motion the user *threw* — a flick, a drag
                           release. A hover highlight seen dozens of times a day
                           should settle flat, so it is critically damped now
                           (`bounce: 0`).
                        2. It was gated on `!active`, so sweeping the cursor
                           across the current page's link made the pill vanish
                           and reappear. The track is continuous now.
                        3. No AnimatePresence meant it popped in from nothing on
                           the first hover and cut out instantly on leave.
                           Nothing in the real world appears from nothing, so it
                           now fades in and out around the shared-layout move. */}
                    <AnimatePresence>
                      {hoveredLink === link.href && (
                        <motion.div
                          key="navHighlight"
                          layoutId="navHighlight"
                          className={`absolute inset-0 rounded-control ${onDarkHero ? 'bg-white/[0.08]' : 'bg-warmOrange/[0.06]'}`}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{
                            layout: { type: 'spring', bounce: 0, duration: 0.25 },
                            opacity: { duration: 0.12, ease: 'easeOut' },
                          }}
                        />
                      )}
                    </AnimatePresence>
                    <span className="relative z-10">{link.label}</span>
                    {active && (
                      <span
                        aria-hidden="true"
                        className={`absolute left-1/2 -translate-x-1/2 bottom-1 w-1 h-1 rounded-full ${onDarkHero ? 'bg-goldLight' : 'bg-warmOrange'}`}
                      />
                    )}
                  </Link>
                )
              })}

              {/* Persistent install ask — the only CTA that exists on every
                  pageview. The homepage converts 5x better than blog CTAs, and
                  this is the one surface shared by both. Non-dismissible by
                  design. */}
              <AppStoreLink
                source="nav"
                className={`ml-2 inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 min-h-[40px] rounded-control transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-warmOrange focus-visible:ring-offset-2 pressable ${
                  onDarkHero
                    ? 'bg-cream text-ink hover:bg-white'
                    : 'bg-charcoal text-white hover:bg-charcoal/90'
                }`}
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
                  <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
                </svg>
                Get the app
              </AppStoreLink>
            </div>
          </div>
        </div>

        {/* Mobile navigation overlay */}
        <div
          className={`lg:hidden fixed inset-0 bg-black/20 backdrop-blur-sm transition-opacity duration-300 ${
            mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          aria-hidden="true"
          onClick={() => setMobileMenuOpen(false)}
        />

        {/* Mobile navigation menu — uses dvh for accurate iOS Safari height.
            `inert` is toggled imperatively in a useEffect (see above), so when
            closed the menu is fully removed from tab order and AT tree. */}
        <div
          ref={menuRef}
          id="mobile-navigation"
          /* Enter 300ms, exit 200ms, both on the iOS drawer curve.
             It was `ease-in-out`, which withholds the first frames — the exact
             moment the user is watching hardest — so the panel *felt* slower
             than its 300ms. An entering surface wants ease-out. The exit is
             deliberately quicker than the entrance: slow where the user is
             deciding, fast where the system is responding. */
          className={`lg:hidden fixed inset-y-0 left-0 w-full sm:max-w-sm bg-cream shadow-2xl transform transition-transform ease-drawer ${
            mobileMenuOpen ? 'translate-x-0 duration-300' : '-translate-x-full duration-200'
          }`}
          style={{ height: '100dvh' }}
          role="dialog"
          aria-modal="true"
          aria-label="Site navigation"
        >
          <div className="h-full flex flex-col">
            <div className="absolute top-2 right-2">
              <button
                ref={closeButtonRef}
                type="button"
                className="tap-target rounded-control inline-flex items-center justify-center text-gray-500 hover:text-charcoal hover:bg-warmOrange/5 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-warmOrange pressable"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close menu"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto pt-20 pb-[max(1.5rem,env(safe-area-inset-bottom))] px-4 sm:px-6">
              <nav className="flex flex-col space-y-1" aria-label="Main">
                {navLinks.map((link) => {
                  const active = isActive(link.href)
                  return (
                    <Link
                      key={link.href}
                      href={link.href}
                      aria-current={active ? 'page' : undefined}
                      className={`px-4 py-4 rounded-control transition-colors duration-200 text-lg pressable-sm ${
                        active
                          ? 'bg-warmOrange/[0.10] text-charcoal font-semibold'
                          : 'text-gray-700 font-medium hover:bg-warmOrange/[0.06] hover:text-charcoal'
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                  )
                })}

                {/* Install CTA pinned into the menu list — mirrors the desktop
                    nav pill so the ask is persistent on mobile too. */}
                <AppStoreLink
                  source="nav_mobile"
                  className="mt-3 inline-flex items-center justify-center gap-2 bg-charcoal text-white text-lg font-medium px-4 py-4 rounded-control hover:bg-charcoal/90 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-warmOrange pressable-sm"
                >
                  <svg className="w-4 h-4" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
                    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
                  </svg>
                  Get the free app
                </AppStoreLink>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </nav>
  )
}
